/**
 * Phone + address traits for the Segment `identify` call (COM-417 item 5).
 *
 * Ports SFRA `segmentLayerHelper.getCustomerAddress` (`:252-277`) and the
 * phone branch of `getSegmentIdentifyEventData` (`:314, :329-341`) for the
 * signed-in, non-checkout case: phone is `profile.phoneHome` formatted with the
 * US country code; the address is the address book's PREFERRED address, falling
 * back to the FIRST address on file. Keys are omitted (never empty) when the
 * source value is absent, exactly like SFRA's `if (x) identifyData.x = x`.
 *
 * SCAPI `getCustomer` already returns `addresses[]` (each with `preferred`) and
 * `phoneHome` on the customer the root loader ships, so no `expand` is needed.
 */
import type { CustomerAddress } from "~/lib/app.types";

import { formatUsPhone } from "./utils";

export type IdentifyContactTraits = {
  phone?: string;
  address_city?: string;
  address_country?: string;
  address_state?: string;
  address_zipcode?: string;
};

/** SFRA: `addressBook.preferredAddress || addresses[0] || null`. */
export function selectIdentifyAddress(
  addresses: readonly CustomerAddress[] | undefined,
): CustomerAddress | null {
  if (!addresses?.length) return null;
  return addresses.find(a => a.preferred) ?? addresses[0] ?? null;
}

export function buildIdentifyContactTraits({
  phoneHome,
  addresses,
}: {
  phoneHome?: string;
  addresses?: readonly CustomerAddress[];
}): IdentifyContactTraits {
  const traits: IdentifyContactTraits = {};
  if (phoneHome) traits.phone = formatUsPhone(phoneHome);
  const address = selectIdentifyAddress(addresses);
  if (address) {
    if (address.city) traits.address_city = address.city;
    // SFRA: `countryCode.value || 'US'` — the key is only set when the address
    // has a countryCode at all, so an empty code falls back to "US".
    if (address.countryCode !== undefined) {
      traits.address_country = address.countryCode || "US";
    }
    if (address.stateCode) traits.address_state = address.stateCode;
    if (address.postalCode) traits.address_zipcode = address.postalCode;
  }
  return traits;
}
